import * as yup from "yup";
import { SCORING_TYPE } from "../models/scoringModel";
import { findOverlappingRangeIndices } from "../components/rules/RangeRulesEditor";
import errorMessages from "./errorMessages";

/**
 * Per-criterion Yup schema for the blocking validations in spec 2 section 7
 * that apply to a single criterion's own config (points > maxScore, RANGE
 * overlap, AI_MATCH completeness). Duplicate-criterionKey and locked-edit
 * checks need sibling/original context this schema doesn't have -- see
 * `validateNoDuplicateKeys`/`validateNoLockedEdits` in `hostValidation.js`,
 * which the host form (admin scenario creator / client recruitment form)
 * must run itself across the full criterion list.
 *
 * Two tiers, deliberately NOT both gated behind `scoringEnabled`:
 *  - Structural / wire-safety (always checked, toggle or no toggle): `points` is a required,
 *    non-nullable number on the wire for every rule type (see ValueScoringRuleRequest /
 *    RangeScoringRuleRequest / SetScoringRuleRequest / AiLevelPointsRequest -- the backend
 *    rejects the *entire save* if any one rule's points is missing, not just that rule), same
 *    for `value` on Value/Set rules. ScoringConfigPanel's own contract explicitly lets every
 *    field be filled in before the toggle is switched on, and apiMapping.js's
 *    isScoringConfigured() sends a criterion whenever anything is configured, regardless of
 *    the toggle -- so an in-progress rule must fail validation whether or not scoring is
 *    enabled yet, or the exact "Punkty" 400 incident this schema was extended for recurs.
 *  - Semantic completeness (only once scoringEnabled is true): maxScore and AI_MATCH's
 *    aiCriterion / minimum 2 levels only matter once the criterion is actually being scored;
 *    they don't block saving an unfinished, still-disabled setup.
 */
export const scoringConfigSchema = yup
    .object()
    .shape({
        valueRules: yup.array().when("scoringType", {
            is: SCORING_TYPE.VALUE,
            then: (s) =>
                s.of(
                    yup.object({
                        value: yup.string().required(errorMessages.valueRequired),
                        points: yup.number()
                            .typeError(errorMessages.pointsRequired)
                            .required(errorMessages.pointsRequired)
                            .test(
                                "points-le-max",
                                errorMessages.pointsExceedMax,
                                function (value) {
                                    return value == null || value <= (this.options.context?.maxScore ?? Infinity);
                                }
                            ),
                    })
                ),
        }),
        rangeRules: yup.array().when("scoringType", {
            is: SCORING_TYPE.RANGE,
            then: (s) =>
                s
                    .of(
                        yup.object({
                            points: yup.number()
                                .typeError(errorMessages.pointsRequired)
                                .required(errorMessages.pointsRequired)
                                .test(
                                    "points-le-max",
                                    errorMessages.pointsExceedMax,
                                    function (value) {
                                        return value == null || value <= (this.options.context?.maxScore ?? Infinity);
                                    }
                                ),
                        })
                    )
                    .test("min-le-max", errorMessages.rangeInverted, (rules) =>
                        (rules ?? []).every((r) => r.min == null || r.min === "" || r.max == null || r.max === "" || Number(r.min) <= Number(r.max))
                    )
                    .test("no-overlap", errorMessages.rangeOverlap, (rules) => findOverlappingRangeIndices(rules).size === 0),
        }),
        setRules: yup.array().when("scoringType", {
            is: SCORING_TYPE.SET,
            then: (s) =>
                s.of(
                    yup.object({
                        value: yup.string().required(errorMessages.valueRequired),
                        points: yup.number().typeError(errorMessages.pointsRequired).required(errorMessages.pointsRequired),
                    })
                ),
        }),
        aiLevels: yup.array().when("scoringType", {
            is: SCORING_TYPE.AI_MATCH,
            then: (s) =>
                s
                    .of(
                        yup.object({
                            points: yup.number()
                                .typeError(errorMessages.pointsRequired)
                                .required(errorMessages.pointsRequired)
                                .test("points-le-max", errorMessages.pointsExceedMax, function (value) {
                                    return value == null || value <= (this.options.context?.maxScore ?? Infinity);
                                }),
                        })
                    )
                    .test("min-levels-when-enabled", errorMessages.aiMatchMissingLevels, function (levels) {
                        // The >=2-levels completeness rule only matters once scoring is actually
                        // turned on for this criterion -- see the tier note above.
                        if (!this.parent?.scoringEnabled) return true;
                        return (levels?.length ?? 0) >= 2;
                    }),
        }),
    })
    .when("scoringEnabled", {
        is: true,
        then: (schema) =>
            schema.shape({
                maxScore: yup.number().nullable().min(0).required(),
                aiCriterion: yup.string().when("scoringType", {
                    is: SCORING_TYPE.AI_MATCH,
                    then: (s) => s.required(errorMessages.aiMatchMissingCriterion),
                }),
            }),
    });
